import React, { useState } from 'react';
import { Modal, Typography, Flex, Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { getDateandTime, parseLocalDate, toYYYYMMDD } from 'src/utils/helpers';
import { MinusCircleFilled } from '@ant-design/icons';
import dayjs from 'dayjs';
import SharedDatePicker from '../DatePicker';
import AntDButton from '../Button';
import { ScrollableContent } from '../ScrollableContent';
import SharedConfirmModal from '../ConfirmModal';
import isBetween from 'dayjs/plugin/isBetween';
import {
  IPoLineModalProps,
  IWorkingHourColumns,
} from 'src/models/ResourceWorkingHourModal';
import en from 'src/messages/en.json';
const { Title, Text } = Typography;

dayjs.extend(isBetween);

const ResourceWorkingHoursModal: React.FC<IPoLineModalProps> = ({
  open,
  onClose,
  data,
  totalDays,
  isManagePO,
  dateRanges,
  onSave,
  configDate,
  isSaving,
}) => {
  const [updatedData, setUpdatedData] = useState(data);
  const [tableData, setTableData] = useState(data);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const normalizedData = Array.isArray(data) ? data : [data];

  normalizedData?.forEach((user, index) => {
    if (!user) return;
    const { name, splitDates } = user;

    if (!name || !splitDates?.length) return;

    splitDates?.forEach((range: any, recIndex: number) => {
      tableData?.push({
        key: `${index}-${recIndex}`,
        name,
        startDate: range?.startDate,
        endDate: range?.endDate,
        approvedHours: range?.timesheetHours?.toString(),
      });
    });
  });
  const handleDateChange = (
    rowIndex: number,
    splitIndex: number,
    field: 'startDate' | 'endDate',
    value: string,
  ) => {
    setUpdatedData((prevData) => {
      const updatedData = [...prevData];
      const rowToUpdate = updatedData[rowIndex];

      if (!rowToUpdate) return prevData;

      const updatedSplitDates = rowToUpdate.splitDates.map(
        (splitDate, index) => {
          if (index === splitIndex) {
            const updatedSplit = {
              ...splitDate,
              [field]: value,
            };

            if (
              field === 'startDate' &&
              value &&
              splitDate.endDate &&
              new Date(value) > new Date(splitDate.endDate)
            ) {
              updatedSplit.endDate = null;
            }

            if (
              field === 'endDate' &&
              value &&
              splitDate.startDate &&
              new Date(value) < new Date(splitDate.startDate)
            ) {
              updatedSplit.startDate = null;
            }

            return updatedSplit;
          }

          return splitDate;
        },
      );

      updatedData[rowIndex] = {
        ...rowToUpdate,
        splitDates: updatedSplitDates,
      };

      return updatedData;
    });
  };

  const handleRemoveSplitDate = (rowIndex: number, splitIndex: number) => {
    setUpdatedData((prevData) => {
      const updatedData = [...prevData];

      const rowToUpdate = updatedData[rowIndex];

      if (!rowToUpdate) return prevData;

      const updatedSplitDates = rowToUpdate.splitDates.filter(
        (_, index) => index !== splitIndex,
      );

      updatedData[rowIndex] = {
        ...rowToUpdate,
        splitDates: updatedSplitDates,
      };

      return updatedData;
    });
  };

  React.useEffect(() => {
    const resData: any = data;

    if (!isManagePO) {
      const formattedData = resData?.dateRanges?.map((item) => {
        const updatedSplits = {
          ...item,
          startDate: item.startDate ? toYYYYMMDD(item.startDate) : '',
          endDate: item.endDate ? toYYYYMMDD(item.endDate) : '',
        };

        return {
          ...item,
          splitDates: updatedSplits,
        };
      });

      setTableData(formattedData);
    } else {
      const formattedUpdatedData = data?.map((item) => {
        if (!item || !item.splitDates) return item;

        const updatedSplits = item.splitDates.map((splitDate) => ({
          ...splitDate,
          startDate: splitDate.startDate ? toYYYYMMDD(splitDate.startDate) : '',
          endDate: splitDate.endDate ? toYYYYMMDD(splitDate.endDate) : '',
          initialStartDate: splitDate.startDate
            ? toYYYYMMDD(splitDate.startDate)
            : '',
          initialEndDate: splitDate.endDate
            ? toYYYYMMDD(splitDate.endDate)
            : '',
        }));

        return {
          ...item,
          splitDates: updatedSplits,
        };
      });

      setUpdatedData(formattedUpdatedData);
    }
  }, [data]);

  const managePoColumns = [
    {
      title: en.POs.Title.fromDate,
      dataIndex: 'startDate',
      key: 'startDate',
      width: '25%',
      render: (startDate, record, rowIndex) => (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
        >
          {(record.splitDates || []).map((splitDate, splitIndex) => (
            <div
              key={splitIndex}
              style={{
                minHeight: '2.5rem',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <SharedDatePicker
                date={
                  splitDate.startDate &&
                  parseLocalDate(splitDate.startDate) instanceof Date &&
                  !isNaN(parseLocalDate(splitDate.startDate).getTime())
                    ? parseLocalDate(splitDate.startDate)
                    : null
                }
                onChange={(date) =>
                  handleDateChange(
                    rowIndex,
                    splitIndex,
                    'startDate',
                    date ? dayjs(date).format('YYYY-MM-DD') : null,
                  )
                }
                mode="date"
                placeholder={en.Timesheet.Placeholder.selectStartDate}
                dateFormat="MM/DD/YYYY"
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' || e.key === 'Delete') {
                    handleDateChange(rowIndex, splitIndex, 'startDate', null);
                  }
                }}
                configDate={configDate}
                disabled={splitDate.status}
                excludeDateIntervals={
                  !splitDate.status
                    ? dateRanges.flatMap(({ start, end }) => {
                        const rangeStart = dayjs(start);
                        const rangeEnd = dayjs(end);
                        const splitStart = dayjs(splitDate.initialStartDate);
                        const splitEnd = dayjs(splitDate.initialEndDate);

                        // No overlap — keep full range
                        if (
                          rangeEnd.isBefore(splitStart) ||
                          rangeStart.isAfter(splitEnd)
                        ) {
                          return [
                            {
                              start: rangeStart.toDate(),
                              end: rangeEnd.toDate(),
                            },
                          ];
                        }

                        const result: { start: Date; end: Date }[] = [];

                        if (rangeStart.isBefore(splitStart)) {
                          result.push({
                            start: rangeStart.toDate(),
                            end: splitStart.subtract(1, 'day').toDate(),
                          });
                        }

                        if (rangeEnd.isAfter(splitEnd)) {
                          result.push({
                            start: splitEnd.add(1, 'day').toDate(),
                            end: rangeEnd.toDate(),
                          });
                        }

                        return result;
                      })
                    : dateRanges
                }
                allowClear={splitDate?.startDate ? null : undefined}
                style={{ zIndex: 900 }}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      title: en.POs.Title.toDate,
      dataIndex: 'endDate',
      key: 'endDate',
      width: '25%',
      render: (_endDate, record, rowIndex) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(record.splitDates || []).map((splitDate, splitIndex) => (
            <div
              key={splitIndex}
              style={{ minHeight: 40, display: 'flex', alignItems: 'center' }}
            >
              <SharedDatePicker
                date={
                  splitDate.endDate &&
                  parseLocalDate(splitDate.endDate) instanceof Date &&
                  !isNaN(parseLocalDate(splitDate.endDate).getTime())
                    ? parseLocalDate(splitDate.endDate)
                    : null
                }
                onChange={(date) =>
                  handleDateChange(
                    rowIndex,
                    splitIndex,
                    'endDate',
                    date ? dayjs(date).format('YYYY-MM-DD') : null,
                  )
                }
                mode="date"
                placeholder={en.Timesheet.Placeholder.selectEndDate}
                dateFormat="MM/DD/YYYY"
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' || e.key === 'Delete') {
                    handleDateChange(rowIndex, splitIndex, 'endDate', null);
                  }
                }}
                disabled={splitDate.status}
                // excludeDateIntervals={dateRanges}
                excludeDateIntervals={
                  !splitDate.status
                    ? dateRanges.flatMap(({ start, end }) => {
                        const rangeStart = dayjs(start);
                        const rangeEnd = dayjs(end);
                        const splitStart = dayjs(splitDate.initialStartDate);
                        const splitEnd = dayjs(splitDate.initialEndDate);

                        if (
                          rangeEnd.isBefore(splitStart) ||
                          rangeStart.isAfter(splitEnd)
                        ) {
                          return [
                            {
                              start: rangeStart.toDate(),
                              end: rangeEnd.toDate(),
                            },
                          ];
                        }

                        const result: { start: Date; end: Date }[] = [];

                        if (rangeStart.isBefore(splitStart)) {
                          result.push({
                            start: rangeStart.toDate(),
                            end: splitStart.subtract(1, 'day').toDate(),
                          });
                        }

                        if (rangeEnd.isAfter(splitEnd)) {
                          result.push({
                            start: splitEnd.add(1, 'day').toDate(),
                            end: rangeEnd.toDate(),
                          });
                        }

                        return result;
                      })
                    : dateRanges
                }
                allowClear={splitDate?.endDate ? null : undefined}
              />
            </div>
          ))}
        </div>
      ),
    },
    {
      title: '',
      key: 'action',
      width: '10%',
      render: (text, record, index) => {
        const splitEndDates = record?.splitDates?.map(
          (dateObj) => dateObj.status,
        );
        const splitStatuses = record?.splitDates?.map(
          (dateObj) => dateObj.status,
        );

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              alignItems: 'center',
            }}
          >
            {splitEndDates?.map((status, idx) => (
              <div
                key={idx}
                style={{
                  minHeight: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <MinusCircleFilled
                  style={{
                    color: status ? '#d9d9d9' : '#ff4d4f',
                    cursor: status ? 'not-allowed' : 'pointer',
                  }}
                  disabled={splitStatuses?.[idx]}
                  title={status ? '' : 'Remove Dates'}
                  onClick={() => {
                    if (!status) {
                      handleRemoveSplitDate(index, idx);
                    }
                  }}
                />
              </div>
            ))}
          </div>
        );
      },
    },
    {
      title: en.Common.Title.status,
      dataIndex: 'status',
      key: 'status',
      width: '20%',
      render: (status, record, index) => {
        const splitEndDates = record?.splitDates?.map(
          (dateObj) => dateObj.status,
        );

        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
          >
            {splitEndDates?.map((status, idx) => (
              <div
                key={idx}
                style={{
                  minHeight: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  paddingLeft: '0.5rem',
                  fontSize: '0.875rem',
                  fontWeight: 400,
                  color: status === true ? '#389e0d' : '#cf1322',
                }}
              >
                {status === true ? 'Received' : 'Not Received'}
              </div>
            ))}
          </div>
        );
      },
    },
  ];

  const poReceivingColumns: ColumnsType<IWorkingHourColumns> = [
    {
      title: 'From Date',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      width: 100,
    },
    {
      title: 'To Date',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => dayjs(date).format('MM/DD/YYYY'),
      width: 100,
    },
    {
      title: 'Approved Hours (Not Received)',
      dataIndex: 'timesheetHours',
      key: 'timesheetHours',
      width: 200,
    },
  ];
  const handleClose = () => {
    if (data[0].splitDates.length !== updatedData[0].splitDates.length) {
      setShowConfirmModal(true);
    } else {
      onClose();
    }
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    setTimeout(() => {
      onClose();
    }, 100);
  };

  const handleCancel = () => {
    setShowConfirmModal(false);
  };

  return (
    <Modal
      open={open}
      onCancel={isManagePO ? handleClose : onClose}
      footer={null}
      maskClosable={false}
      centered
      title={
        <Flex vertical gap="middle" style={{ padding: '16px' }}>
          {(isManagePO === false ? normalizedData : updatedData)?.map(
            (user, index) => (
              <Flex
                key={index}
                justify="space-between"
                style={{ width: '70%' }}
                align="center"
              >
                <Title style={{ textAlign: 'left', margin: 0 }} level={4}>
                  {user?.name}
                </Title>

                {isManagePO === false && (
                  <Text style={{ fontWeight: 'normal' }}>
                    Total: {totalDays} Days ({user?.hours} hours)
                  </Text>
                )}
              </Flex>
            ),
          )}
        </Flex>
      }
    >
      <div style={{ padding: '0 1rem 1rem' }}>
        <ScrollableContent minHeight="100%" maxHeight="100%">
          <Table
            rowKey="id"
            columns={isManagePO ? managePoColumns : poReceivingColumns}
            dataSource={isManagePO ? updatedData : tableData}
            pagination={false}
            bordered={false}
            sticky
            size="small"
            loading={isSaving}
          />
        </ScrollableContent>
        {isManagePO && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              paddingTop: '0.5rem',
            }}
          >
            <AntDButton
              type="primary"
              status="Saving..."
              statusOnComplete="Save"
              onClick={() => onSave(updatedData)}
              htmlType="button"
            ></AntDButton>
          </div>
        )}

        {showConfirmModal && (
          <SharedConfirmModal
            show={showConfirmModal}
            onCancel={handleCancel}
            onConfirm={handleConfirm}
            title={en.Common.Title.alert}
            description={
              'You have unsaved changes. Are you sure you want to exit without saving?'
            }
            style={{
              backgroundColor: '#D9534F',
              color: 'white',
              borderColor: '#D9534F',
            }}
          />
        )}
      </div>
    </Modal>
  );
};

export default ResourceWorkingHoursModal;
